import React, { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchBar } from '../components/SearchBar';
import { RestaurantCard } from '../components/RestaurantCard';
import { MapView } from '../components/MapView';
import { AskAI } from '../components/AskAI';
import { mockRestaurants } from '../utils/mockData';
import { Map, List, Filter } from 'lucide-react';
import { Grade } from '../types';
interface HomePageProps {
  onSearch: (query: string) => void;
  onSelectRestaurant: (id: string) => void;
}
export function HomePage({
  onSearch,
  onSelectRestaurant
}: HomePageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeaheadQuery, setTypeaheadQuery] = useState('');
  const [selectedGrade, setSelectedGrade] = useState<Grade | 'All'>('All');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInitialQuestion, setAiInitialQuestion] = useState('');
  const filteredRestaurants = useMemo(() => {
    return mockRestaurants.filter(r => {
      const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase()) || r.cuisine.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesGrade = selectedGrade === 'All' || r.currentGrade === selectedGrade;
      return matchesSearch && matchesGrade;
    });
  }, [searchQuery, selectedGrade]);
  // Typeahead suggestions — filter all restaurants by the live typing query
  const typeaheadSuggestions = useMemo(() => {
    if (!typeaheadQuery || typeaheadQuery.length < 1) return [];
    const q = typeaheadQuery.toLowerCase();
    return mockRestaurants.filter(r => r.name.toLowerCase().includes(q) || r.cuisine.toLowerCase().includes(q) || r.address.toLowerCase().includes(q));
  }, [typeaheadQuery]);
  const handleMarkerClick = (id: string) => {
    setSelectedId(id);
  };
  const handleAskAI = (query: string) => {
    setAiInitialQuestion(query);
    setAiOpen(true);
  };
  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    setTypeaheadQuery('');
  }, []);
  const handleTypeaheadChange = useCallback((q: string) => {
    setTypeaheadQuery(q);
  }, []);
  const handleSuggestionSelect = useCallback((id: string) => {
    setTypeaheadQuery('');
    onSelectRestaurant(id);
  }, [onSelectRestaurant]);
  const grades: (Grade | 'All')[] = ['All', 'A', 'B', 'C', 'F'];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex flex-col md:flex-row h-full w-full overflow-hidden relative" data-id="element-167">
      {/* Mobile Floating Search & Controls */}
      <div className="md:hidden absolute top-4 left-4 right-4 z-20 flex flex-col gap-3 pointer-events-none" data-id="element-168">
        <div className="pointer-events-auto shadow-lg rounded-2xl" data-id="element-169">
          <SearchBar initialQuery={searchQuery} onSearch={handleSearch} onAskAI={handleAskAI} onQueryChange={handleTypeaheadChange} onSelectSuggestion={handleSuggestionSelect} suggestions={typeaheadSuggestions} className="w-full" data-id="element-170" />
        </div>

        <div className="flex justify-between items-center pointer-events-auto" data-id="element-171">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar" data-id="element-172">
            {grades.map(grade => <button key={grade} onClick={() => setSelectedGrade(grade)} className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap shadow-sm transition-colors ${selectedGrade === grade ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-id="element-173">
                {grade === 'All' ? 'All Grades' : `Grade ${grade}`}
              </button>)}
          </div>
        </div>
      </div>

      {/* Mobile View Toggle */}
      <button onClick={() => setMobileView(v => v === 'map' ? 'list' : 'map')} className="md:hidden absolute bottom-6 left-1/2 -translate-x-1/2 z-30 bg-slate-900 text-white px-6 py-3 rounded-full font-semibold shadow-xl flex items-center gap-2 hover:bg-slate-800 transition-colors" data-id="element-174">
        {mobileView === 'map' ? <>
            <List className="w-5 h-5" data-id="element-175" /> Show List
          </> : <>
            <Map className="w-5 h-5" data-id="element-176" /> Show Map
          </>}
      </button>

      {/* LEFT PANEL (Desktop List View / Mobile Full List) */}
      <div className={`
          ${mobileView === 'list' ? 'flex' : 'hidden md:flex'} 
          flex-col w-full md:w-[420px] lg:w-[480px] h-full bg-slate-50 border-r border-slate-200 z-10 shrink-0
          absolute md:relative inset-0
        `} data-id="element-177">
        {/* Desktop Search Header */}
        <div className="hidden md:block p-4 bg-white border-b border-slate-200 shrink-0 relative z-30" data-id="element-178">
          <SearchBar initialQuery={searchQuery} onSearch={handleSearch} onAskAI={handleAskAI} onQueryChange={handleTypeaheadChange} onSelectSuggestion={handleSuggestionSelect} suggestions={typeaheadSuggestions} className="mb-4" data-id="element-179" />
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar" data-id="element-180">
            <Filter className="w-4 h-4 text-slate-400 shrink-0 mr-1" data-id="element-181" />
            {grades.map(grade => <button key={grade} onClick={() => setSelectedGrade(grade)} className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${selectedGrade === grade ? 'bg-teal-100 text-teal-800 ring-1 ring-teal-600/20' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-182">
                {grade === 'All' ? 'All' : grade}
              </button>)}
          </div>
        </div>

        {/* Mobile List Header Spacer */}
        <div className="md:hidden h-32 shrink-0 bg-transparent" data-id="element-183" />

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50 md:bg-transparent" data-id="element-184">
          <div className="flex items-center justify-between mb-2" data-id="element-185">
            <h2 className="font-bold text-slate-900" data-id="element-186">
              {filteredRestaurants.length} Restaurants
            </h2>
          </div>

          {filteredRestaurants.length === 0 ? <div className="text-center py-12" data-id="element-187">
              <p className="text-slate-500" data-id="element-188">
                No restaurants found matching your criteria.
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setSelectedGrade('All');
          }} className="mt-4 text-teal-600 font-medium hover:underline" data-id="element-189">
                Clear filters
              </button>
            </div> : <div className="grid grid-cols-1 gap-4 pb-24 md:pb-4" data-id="element-190">
              {filteredRestaurants.map((restaurant, idx) => <div key={restaurant.id} onMouseEnter={() => setHoveredId(restaurant.id)} onMouseLeave={() => setHoveredId(null)} className={`transition-all duration-200 rounded-2xl ${selectedId === restaurant.id ? 'ring-2 ring-teal-500 shadow-md' : hoveredId === restaurant.id ? 'ring-1 ring-teal-500/50' : ''}`} data-id="element-191">
                  <RestaurantCard restaurant={restaurant} onClick={() => onSelectRestaurant(restaurant.id)} index={idx} data-id="element-192" />
                </div>)}
            </div>}
        </div>
      </div>

      {/* RIGHT PANEL (Map) */}
      <div className={`
          ${mobileView === 'map' ? 'block' : 'hidden md:block'} 
          flex-1 h-full relative bg-slate-200
        `} data-id="element-193">
        <MapView restaurants={filteredRestaurants} selectedId={selectedId || hoveredId} onSelectRestaurant={handleMarkerClick} onViewDetails={onSelectRestaurant} data-id="element-194" />
      </div>

      {/* General AI Chat */}
      <AskAI isOpen={aiOpen} onClose={() => {
      setAiOpen(false);
      setAiInitialQuestion('');
    }} initialQuestion={aiInitialQuestion} data-id="element-195" />
    </motion.div>;
}